import React, {useState, useRef, useEffect} from 'react'

interface TextBoxExpandProps {
    /**
     * The content to be displayed - can be either a string or a React node
     */
    content: string | React.ReactNode
    /**
     * Maximum number of lines to show when collapsed
     * @default 3
     */
    maxLines?: number
    /**
     * Text to show in the expand button
     * @default "Show more"
     */
    expandText?: string
    /**
     * Text to show in the collapse button
     * @default "Show less"
     */
    collapseText?: string
    /**
     * Custom CSS class name
     */
    className?: string
    /**
     * Custom CSS class for the button
     */
    buttonClassName?: string
    /**
     * Data test ID for testing
     */
    dataTestId?: string
}

/**
 * TextBoxExpand - A component that shows content with the ability to expand/collapse
 * based on a specified number of lines
 */
export const TextBoxExpand: React.FC<TextBoxExpandProps> = ({
    content,
    maxLines = 3,
    expandText = 'Show more',
    collapseText = 'Show less',
    className = '',
    buttonClassName = '',
    dataTestId
}) => {
    const [isExpanded, setIsExpanded] = useState(false)
    const [shouldShowButton, setShouldShowButton] = useState(false)
    const contentRef = useRef<HTMLDivElement>(null)

    useEffect(() => {
        const checkContentHeight = () => {
            if (contentRef.current) {
                const lineHeight = parseInt(getComputedStyle(contentRef.current).lineHeight) || 20
                const maxHeight = lineHeight * maxLines
                const scrollHeight = contentRef.current.scrollHeight

                // Only show the button if content exceeds maxLines
                setShouldShowButton(scrollHeight > maxHeight)
            }
        }

        checkContentHeight()

        // Re-check on window resize
        window.addEventListener('resize', checkContentHeight)
        return () => window.removeEventListener('resize', checkContentHeight)
    }, [content, maxLines])

    const toggleExpand = () => {
        setIsExpanded(!isExpanded)
    }

    return (
        <div className={className} data-test-id={dataTestId}>
            <div
                ref={contentRef}
                style={{
                    overflow: 'hidden',
                    maxHeight: isExpanded ? 'none' : `${maxLines * 1.5}em`,
                    transition: 'max-height 0.3s ease-out'
                }}
            >
                {content}
            </div>

            {shouldShowButton && (
                <button
                    onClick={toggleExpand}
                    className={buttonClassName}
                    data-test-id={dataTestId ? `${dataTestId}-button` : undefined}
                    type="button"
                >
                    {isExpanded ? collapseText : expandText}
                </button>
            )}
        </div>
    )
}

export default TextBoxExpand
